/**
 * As AccessManagement puts it on the wire.
 *
 * String members, and the engine's own names. `GroupResponseV1` carries
 * `[JsonConverter(typeof(JsonStringEnumConverter))]`, so every group this console reads arrives as
 * `"Self" | "Household" | "Event"`; modelling it as `Single = 0, Household = 1` made
 * `group.groupType === GroupType.Household` the comparison `"Household" === 1`, false for every group
 * ever loaded. That decided whether the household relationship picker rendered, whether a household went down
 * the household-teardown path or the plain group delete, and what every row's type tag said.
 *
 * `Event` is here because the engine has it; `Single` is gone because the engine calls it `Self`.
 */
export enum GroupType {
  Self = 'Self',
  Household = 'Household',
  Event = 'Event'
}

export enum HouseholdRelationship {
  Owner = 'Owner',
  Member = 'Member',
  Dependent = 'Dependent'
}

export type DeleteMode = 'Soft' | 'Hard';

export interface EntitySummaryV1 {
  id: string;
  name: string;
}

export interface GroupMemberSummaryV1 extends EntitySummaryV1 {
  roleId: string;
  roleName: string | null;
  householdRelationship: HouseholdRelationship;
  guardianUserIds: string[];
  dependentUserIds: string[];
}

export interface BaseResponse {
  createdOn: Date;
  modifiedOn: Date;
  archivedOn?: Date;
  isActive: boolean;
}

export interface CreateUserRequestV1 {
  externalId: string;
  email: string;
  name: string;
  isActive: boolean;
}

export interface CreateAccountRequestV1 {
  userId: string;
  name: string;
  description?: string;
  isActive: boolean;
}

export interface CreateGroupRequestV1 {
  paymentAccountId: string;
  name: string;
  description?: string;
  groupType: GroupType;
  isActive: boolean;

  /**
   * The founding members, which a household must name and an event group may.
   *
   * Access Management refuses a household naming anything other than exactly one Owner, because a
   * household with no Owner is unrecoverable: the Owner bundle is the only thing that can hand out the
   * roster capabilities, so nobody could ever be given them. Every founding member must also own the
   * account paying for the group -- a create asks nobody to consent, so it cannot enroll a stranger.
   */
  members?: GroupMemberRequestV1[];
}

export interface GroupMemberRequestV1 {
  userId: string;
  roleId?: string;
  householdRelationship?: HouseholdRelationship;
}

/**
 * Moves a member onto a named role. The name rather than the id, because the engine resolves it against
 * the active roles and answers 400 for one that no active role carries, which is the same refusal an
 * operator needs whether they typed a stale name or picked a role that has since been retired.
 *
 * Owner is not a value this accepts. Ownership follows the household relationship, so the engine refuses
 * the name outright rather than moving the bundle.
 */
export interface SetMemberRoleRequestV1 {
  roleName: string;
}

export interface UserResponseV1 extends BaseResponse {
  userId: string;
  externalId: string;
  email: string;
  name: string;
  groups: EntitySummaryV1[];
  scopes: EntitySummaryV1[];
}

export interface UserFormModel {
  userId?: string;
  externalId: string;
  email: string;
  name: string;
  isActive: boolean;
  groupIds?: string[];
}

export interface UserAccountFormModel {
  externalId: string;
  email: string;
  name: string;
  accountName: string;
  accountDescription: string;
  groupName: string;
  groupDescription: string;
}

export interface AccountResponseV1 extends BaseResponse {
  accountId: string;
  userId: string;
  walletId: string;
  name: string;
  description?: string;
  user?: UserResponseV1;
  groups: EntitySummaryV1[];
}

export interface AccountFormModel {
  accountId?: string;
  userId: string;
  name: string;
  description: string;
  isActive: boolean;
}

export interface AccountWithUser extends AccountResponseV1 {
  userName?: string;
  userEmail?: string;
}

export interface GroupResponseV1 extends BaseResponse {
  groupId: string;
  paymentAccountId: string;
  name: string;
  description?: string;
  groupType: GroupType;
  account?: AccountResponseV1;
  members: GroupMemberSummaryV1[];
  accountIds?: string[];
  memberUserIds?: string[];
}

export interface GroupFormModel {
  groupId?: string;
  paymentAccountId: string;
  name: string;
  description: string;
  groupType: GroupType;
  isActive: boolean;
  memberIds?: string[];
}

/**
 * A group plus its payment account holder. Distinct from the household Owner: the account that pays is
 * not necessarily the member holding HouseholdRelationship.Owner, so these are deliberately not named owner*.
 */
export interface GroupWithPaymentAccount extends GroupResponseV1 {
  paymentAccountName?: string;
  paymentAccountEmail?: string;
  accountCount?: number;
}

export interface GroupMemberResponseV1 {
  groupMemberId: string;
  groupId: string;
  userId: string;
  roleId: string;

  /**
   * The role's name as the engine resolved it, which is the only name available for a role the role
   * list no longer carries.
   *
   * `GET /v1/roles` excludes archived rows, so a member still holding an archived role resolves to
   * nothing in that list; this name keeps them on the roster, with a picker to move them off it.
   */
  roleName?: string | null;
  householdRelationship: HouseholdRelationship;
  /**
   * What this member may actually do in the group: their role's bundle plus their grants, minus
   * their denies. The engine computes it, so a surface reading it cannot disagree with the gate.
   *
   * Populated by the roster read (`getGroupMembers`) and empty on every other path, the household
   * admin grant and revoke responses among them. So a caller patching a roster row in place from a
   * mutation response turns every capability on that row off, and the screens here replace the whole
   * roster after a write for that reason rather than out of caution.
   */
  scopes: string[];
  guardianUserIds: string[];
  dependentUserIds: string[];
  createdOn: Date;
  modifiedOn: Date;
  group?: GroupResponseV1;
  user?: UserResponseV1;
  role?: RoleResponseV1;
}

export interface HouseholdMemberResponseV1 {
  youtopianId: string;
  name: string;
  householdRelationship: HouseholdRelationship;
  guardianUserIds: string[];
  dependentUserIds: string[];
}

export interface UserGroupResponseV1 {
  groupMemberId: string;
  groupId: string;
  groupName: string;
  userId: string;
  roleId: string;
  roleName: string;
  createdOn: Date;
  modifiedOn: Date;
}

export interface GroupMemberFormModel {
  groupId: string;
  userId: string;
  roleId: string;
}

export interface RoleResponseV1 extends BaseResponse {
  roleId: string;
  name: string;
  description?: string;
  isDefault: boolean;
  scopes: EntitySummaryV1[];
  scopeIds?: string[];
}

export interface ScopeResponseV1 extends BaseResponse {
  scopeId: string;
  namespace: string;
  resource: string | null;
  action: string | null;
  fullPath: string;
  displayName: string;
  description?: string;
  source: number;

  /**
   * Whether this scope may be handed to somebody who was not born holding it.
   *
   * Five are not: Members.Manage is delegation itself, Group.Manage can archive the household out from
   * under its members, Guardianship.Manage moves a dependent's health-sharing grants,
   * Memberships.Purchase spends the household's payment account, and Orders.Guests is the group's money
   * with no member to attribute it to. Access Management refuses all five on a role bundle as well as
   * on a grant, and a bundle is assignable to any member, so this is the flag that keeps the picker
   * from offering staff a write the engine will reject.
   */
  isDelegable: boolean;
  isDefault: boolean;
}

/**
 * Which direction a permission row points. A grant adds the scope; a deny takes it away, including
 * one the member holds through their role bundle, which no revocation can reach.
 *
 * Serialized as a string by the engine, so the values are the names rather than the ordinals.
 */
export enum PermissionEffect {
  Allow = 'Allow',
  Deny = 'Deny'
}

/** The kind of object a permission row is written over, matching the backend's grant namespaces. */
export enum PermissionObjectNamespace {
  Account = 'account',
  User = 'user',
  Group = 'group'
}

export interface PermissionResponseV1 {
  permissionId: string;
  grantorUserId: string;
  granteeUserId: string;
  scopeId: string;
  /**
   * The object the row was written over: a household, or the member themselves. Two rows naming the
   * same scope answer different questions depending on this, so a surface listing them says which.
   */
  objectNamespace: PermissionObjectNamespace | string;
  objectId: string;
  /**
   * Whether the row adds the scope or takes it away. Required rather than optional: a reader
   * treating an absent effect as Allow renders a member's denied capability as one they hold, which
   * is the whole reason the engine started sending it.
   */
  effect: PermissionEffect;
  expiresOn?: Date | string | null;
  revokedOn?: Date | string | null;
  createdOn: Date | string;
  modifiedOn: Date | string;
}

export interface RoleScopeResponseV1 {
  roleScopeId: string;
  roleId: string;
  scopeId: string;
  scopeName: string;
  scopeDescription: string;
  createdOn: Date;
  role?: RoleResponseV1;
  scope?: ScopeResponseV1;
}

export interface UserWithGroups extends UserResponseV1 {
  fullGroups?: GroupResponseV1[];
  groupNames?: string;
  accountCount?: number;
}

export interface GroupWithMembers extends GroupResponseV1 {
  fullMembers?: GroupMemberResponseV1[];
  memberNames?: string;
}

export interface RoleWithScopes extends RoleResponseV1 {
  fullScopes?: ScopeResponseV1[];
  scopeNames?: string;
}

export interface CreateUserAccountWithDefaultGroupRequestV1 {
  externalId: string;
  email: string;
  name?: string;
  accountName: string;
  accountDescription?: string;
  groupName: string;
  groupDescription?: string;
}

export interface CreateUserAccountResponseV1 {
  user: UserResponseV1;
  account: AccountResponseV1;
  groups: GroupResponseV1[];
}
